
import Prototype from './Prototype.vue';
import Component from 'vue-class-component';
import i18n from '@/i18n';
import baseProps from '@/baseProps';
import NonCommodityStep from '@/models/NonCommodityStep';
import SessionState from '@/interfaces/SessionState';
import { Prop } from 'vue-property-decorator';
import Url from '@/interfaces/Url';
import IocContainer from '@/container/IocContainer';
import SERVICES from '@/container/Services';

@Component({
  props: { ...baseProps },
  i18n
})
export default class NonCommodityPrototype extends Prototype {
  @Prop({
    default: process.env.VUE_APP_JOURNEY_CAMPAIGN
  })
  public campaign!: string;
  public showSuccess = false;
  protected disableInitialProductLoading = false;
  protected stepStates: NonCommodityStep[] = [];
  protected urlProvider = IocContainer.get<Url>(SERVICES.URL);

  beforeMount(): void {
    if (this.campaign === '' || this.campaign === undefined) {
      if (process.env.VUE_APP_ENVIRONMENT_STAGE !== 'local') {
        throw new Error('Campaign is required - please set a campaign as attribute on the web - component');
      }
      throw new Error(
        'Campaign is required - please set a campaign inside the .env.local file as VUE_APP_JOURNEY_CAMPAIGN'
      );
    }
  }

  created(): void {
    if (
      [
        'elli-thg-audi',
        'elli-thg-vw',
        'elli-thg-vwfs',
        'elli-thg-skoda',
        'elli-thg-cupra',
        'elli-thg-elli',
        'elli-thg-man'
      ].includes(process.env.VUE_APP_ENVIRONMENT)
    ) {
      const fontsVolkswagen =
        "@font-face {font-family: 'VW-Head';src: url('https://uploads-ssl.webflow.com/5f22667768fd8e5cfcbcfdbe/6087ba20b2aec007bf3607aa_VWHeadWeb-Bold.woff2') format('woff2');font-weight: bold;font-style: normal;}@font-face {font-family: 'VW-Head';src: url('https://uploads-ssl.webflow.com/5f22667768fd8e5cfcbcfdbe/6087ba20df0d37292980e901_VWHeadWeb-Regular.woff2') format('woff2');font-weight: normal;font-style: normal;}@font-face {font-family: 'VW-Head';src: url('https://uploads-ssl.webflow.com/5f22667768fd8e5cfcbcfdbe/6087ba201aa0757b648b8d14_VWHeadWeb-Light.woff2') format('woff2');font-weight: normal;font-style: normal;}@font-face {font-family: 'VW-Text';src: url('https://uploads-ssl.webflow.com/5f22667768fd8e5cfcbcfdbe/6087b9555ea3a95fb7ac54b6_VWTextWeb-Bold.woff2') format('woff2');font-weight: bold;font-style: normal;}@font-face {font-family: 'VW-Text';src: url('https://uploads-ssl.webflow.com/5f22667768fd8e5cfcbcfdbe/6087b9553450e6dc6da4f2d0_VWTextWeb-Regular.woff2') format('woff2');font-weight: normal;font-style: normal;}";
      const fontsAudi =
        "@font-face {font-family: 'AudiType-Normal';src: url(https://assets.audi.com/audi-fonts/1/AudiTypeVF.woff2) format('woff2');font-weight: normal;font-style: normal;}@font-face {font-family: 'AudiType-Bold';src: url(https://assets.audi.com/audi-fonts/1/AudiTypeVF.woff2) format('woff2');font-weight: normal;font-style: normal;}";
      const css = fontsVolkswagen + fontsAudi;

      if (!document.getElementById('elliFonts')) {
        const head = document.head || document.getElementsByTagName('head')[0];
        const style = document.createElement('style');
        style.id = 'elliFonts';
        style.type = 'text/css';
        style.innerText = css;
        head.appendChild(style);
      }
    }
  }

  protected getPreviousStep(): NonCommodityStep {
    return NonCommodityStep.getPreviousStep(this.stepStates);
  }

  protected getNextStep(): NonCommodityStep {
    return NonCommodityStep.getNextStep(this.stepStates);
  }

  protected getCurrentStep(): NonCommodityStep {
    return NonCommodityStep.getCurrentStep(this.stepStates);
  }

  protected getStepByWidget(widgetKey: string): NonCommodityStep {
    return NonCommodityStep.getStepByWidget(this.stepStates, widgetKey);
  }

  public onSelectProduct(productData: Record<any, any>): void {
    this.data.selectedProduct = productData;
  }

  public goBack(): void {
    const previousStep: NonCommodityStep = this.getPreviousStep();
    if (previousStep && previousStep.widget) {
      this.goToWidget(previousStep.widget);
    }
  }

  public goToWidget(widgetName: string): void {
    this.getCurrentStep().isShown = false;
    this.getStepByWidget(widgetName).isShown = true;
    this.switchWidget(widgetName);
  }

  get sessionState(): SessionState {
    return this.store.getters.getSession;
  }

  public onStepDone(): void {
    this.scrollToTop();
    const currentStep: NonCommodityStep = this.getCurrentStep();
    const nextStep: NonCommodityStep = this.getNextStep();
    if (nextStep && nextStep.widget) {
      this.widgetsList[nextStep.widget] = true;
      this.widgetsList.disabled[nextStep.widget] = false;
    }
    currentStep.isDone = true;
    if (nextStep && nextStep.widget) {
      this.goToWidget(nextStep.widget);
    }
  }

  public isStepShown(widgetKey: string): boolean {
    return this.widgetsList[widgetKey] === true;
  }

  public async setUpSession(): Promise<void> {
    if (!localStorage.getItem(process.env.VUE_APP_SESSION_STORAGE_KEY)) {
      return await this.createSession();
    } else {
      return await this.loadSessionState();
    }
  }

  public async loadSessionState(): Promise<void> {
    await this.ApiProvider.sessionRead()
      .then((response) => {
        if (response.data.extendedData) {
          response.data.extendedData = JSON.parse(response.data.extendedData);
        }
        if (response.data.subSessions) {
          this.store.dispatch('setSubSessions', response.data.subSessions);
          delete response.data['subSessions'];
        }
        this.store.dispatch('setSession', response.data);
        this.data.isSessionLoaded = true;
      })
      .catch((error) => {
        if (error.response.data && error.response.data == 'Wrong session hash.') {
          return this.createSession();
        }
      });
  }

  /**
   * Can be overridden
   */
  protected beforeCreateSession(): void {
    return;
  }

  public async createSession(): Promise<void> {
    this.beforeCreateSession();
    let data: { campaign: string; uvp?: string; vp?: string } = { campaign: this.$props.campaign };
    if (this.urlProvider.hasKey(window.location.href, 'DealerID')) {
      data = Object.assign({ vpCode: this.urlProvider.getKey(window.location.href, 'DealerID') }, data);
    }
    if (this.urlProvider.hasKey(window.location.href, 'SubID')) {
      data = Object.assign({ uvpCode: this.urlProvider.getKey(window.location.href, 'SubID') }, data);
    }
    if (this.$props.customer && this.$props.customer !== '') {
      data = Object.assign({ customerId: this.$props.customer }, data);
    }
    if (this.urlProvider.hasKey(window.location.href, 'utm_source')) {
      data = Object.assign({ vpCode: this.urlProvider.getKey(window.location.href, 'utm_source') }, data);
    }
    if (this.urlProvider.hasKey(window.location.href, 'utm_id')) {
      data = Object.assign({ uvpCode: this.urlProvider.getKey(window.location.href, 'utm_id') }, data);
    }
    await this.ApiProvider.createSession(data).then((response) => {
      localStorage.setItem(process.env.VUE_APP_SESSION_STORAGE_KEY, response.data.journeySession.sessionHash);
      this.store.dispatch('setSession', response.data);
      this.store.dispatch('setSubSessions', []);
      this.afterSessionCreation();
      this.data.isSessionLoaded = true;
    });
  }

  public afterSessionCreation(): void {
    // Override this if needed
  }

  public loadApp(): void {
    if (!this.disableInitialProductLoading) {
      this.loadProducts();
    } else {
      this.data.loadingProducts = false;
    }
    this.loadSettings();
  }

  public loadProducts(): void {
    this.data.loadingProducts = true;
    this.ApiProvider.getProducts()
      .then((response) => {
        const products = response.data.map((product) => {
          product.initialDurationType = this.getDurationType(product.initialDurationType, product.initialDuration);
          product.renewalDurationType = this.getDurationType(product.renewalDurationType, product.renewalDuration);
          product.cancellationPeriodDaysType = this.getDurationType(
            product.cancellationPeriodDaysType,
            product.cancellationPeriodDays
          );
          product.fixedPriceMonthsType = this.getDurationType('MONTHS', product.fixedPriceMonths);

          return product;
        });

        this.store.dispatch('setAvailableProducts', products);
      })
      .catch((error) => {
        console.error('Could not load any products.');
      })
      .finally(() => {
        this.data.loadingProducts = false;
        this.deactivateSpinnerActive();
      });
  }

  protected setStepsDoneLocked(): void {
    for (let i = 0; i < this.stepStates.length; ++i) {
      this.stepStates[i].isLocked = true;
    }
    this.getCurrentStep().isDone = true;
  }

  public restartJourney(): void {
    location.reload();
  }

  public orderFailed(): void {
    this.showSuccess = false;
    this.setStepsDoneLocked();
    this.invalidateSession();
  }

  public orderCreated(): void {
    this.showSuccess = true;
    this.setStepsDoneLocked();
    this.invalidateSession();
  }

  protected invalidateSession(): void {
    localStorage.removeItem(process.env.VUE_APP_SESSION_STORAGE_KEY);
  }
}
